import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Save } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";

/** Настройки 1С для документов поставки.
 *
 *  Заказ покупателя, паллеты, ПТМО и счёт-фактура собираются из полутора десятков ссылок на
 *  объекты 1С — организация, банковский счёт, контрагент, договор, хозоперация, подписи. В коде
 *  их держать нельзя: у реплики и боевой базы GUID'ы разные, а состав юрлиц меняется.
 *
 *  Экран правит тот же конфиг, которым уже пользуется ручной поток заказов, — второго набора
 *  настроек у цикла поставок нет. Разделы: `all` (общие реквизиты), `orgs` (юрлица),
 *  `contrs`/`contracts` (контрагенты и договоры по маркетплейсам), `full_cycle` (ПТМО и
 *  счёт-фактура).
 *
 *  ⚠️ Юрлица в конфиге записаны КАПСОМ — так их пишет бот, и портал приводит к тому же виду.
 */
export function OnecSettingsPage(): React.ReactElement {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["supply", "order-config"],
    queryFn: async () => (await api.get<{ config: Config }>("/supply/order-config")).data.config,
  });
  const [draft, setDraft] = useState<Config | null>(null);
  useEffect(() => {
    if (q.data) setDraft(q.data);
  }, [q.data]);

  const save = useMutation({
    mutationFn: async (config: Config) =>
      (await api.put<{ config: Config }>("/supply/order-config", { config })).data.config,
    onSuccess: (config) => {
      qc.setQueryData(["supply", "order-config"], config);
      toast.success("Настройки 1С сохранены");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Сохранить не удалось")),
  });

  const set = (path: string[], value: unknown): void => {
    if (!draft) return;
    const copy: Config = JSON.parse(JSON.stringify(draft)) as Config;
    let node: Record<string, unknown> = copy as Record<string, unknown>;
    for (const step of path.slice(0, -1)) node = node[step] as Record<string, unknown>;
    node[path[path.length - 1]!] = value;
    setDraft(copy);
  };

  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(q.data);

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold">Настройки 1С</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Ссылки на объекты 1С для документов поставки: заказ покупателя, паллеты, ПТМО,
            счёт-фактура. Значения проверены на боевых документах. Пустое поле — отказ при создании
            документа, а не «как-нибудь»: собранный наугад документ проведут, и разбираться будут в
            учёте.
          </p>
        </div>
        <Button
          className="ml-auto"
          disabled={!dirty || save.isPending}
          onClick={() => draft && save.mutate(draft)}
        >
          <Save className={cn("size-4", save.isPending && "animate-pulse")} /> Сохранить
        </Button>
        {q.isFetching && <RefreshCw className="size-4 animate-spin text-muted-foreground" />}
      </div>

      {draft &&
        SECTIONS.map(({ key, title, hint }) => (
          <section key={key} className="rounded-md border bg-card">
            <div className="border-b px-3 py-2">
              <b>{title}</b>
              {hint && <span className="ml-2 text-xs text-muted-foreground">{hint}</span>}
            </div>
            <div className="divide-y">
              {flatten(draft[key], [key]).map(({ path, value }) => (
                <div key={path.join(".")} className="flex flex-wrap items-center gap-2 px-3 py-1.5">
                  <span className="w-96 shrink-0 text-xs" title={path.join(" → ")}>
                    {path.slice(1).join(" · ")}
                  </span>
                  {/* тип поля бережём: текстовым вводом «true» ломался с первой же буквы, а
                      пустое число уезжало нулём — и то и другое всплыло бы уже в документе */}
                  {typeof value === "boolean" ? (
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => set(path, e.target.checked)}
                      />
                      {value ? "да" : "нет"}
                    </label>
                  ) : typeof value === "number" ? (
                    <input
                      type="number"
                      className="h-8 w-32 rounded border border-input bg-background px-2 font-mono text-xs"
                      value={value}
                      onChange={(e) =>
                        e.target.value !== "" && set(path, Number(e.target.value))
                      }
                    />
                  ) : (
                    <input
                      className="h-8 min-w-0 flex-1 rounded border border-input bg-background px-2 font-mono text-xs"
                      value={String(value)}
                      onChange={(e) => set(path, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
    </div>
  );
}

type Config = Record<string, Record<string, unknown>>;

const SECTIONS: { key: string; title: string; hint?: string }[] = [
  { key: "all", title: "Общие реквизиты заказа", hint: "вид цен, состояние, ответственный, склад резерва" },
  { key: "orgs", title: "Юрлица-продавцы", hint: "организация и банковский счёт; названия КАПСОМ" },
  { key: "contrs", title: "Контрагенты маркетплейсов" },
  { key: "contracts", title: "Договоры", hint: "по юрлицу и маркетплейсу" },
  { key: "full_cycle", title: "ПТМО и счёт-фактура", hint: "передача фактически бронирует товар на юрлице" },
];

/** Вложенный конфиг — плоским списком «путь → значение»: полей полтора десятка, и форма на
 *  каждый случай была бы больше самого конфига. */
function flatten(node: unknown, path: string[]): { path: string[]; value: unknown }[] {
  if (node === null || typeof node !== "object") return [{ path, value: node }];
  return Object.entries(node as Record<string, unknown>).flatMap(([key, value]) =>
    flatten(value, [...path, key]),
  );
}
